<script>
import { DateTime } from 'luxon';
import NewsRecordModel from '@models/NewsRecord';
import BlockingContainer from '@components/BlockingContainer';
import FileDownloadLink from '@components/FileDownloadLink';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Layout from '@layouts/main';

export default {
  name: 'NewsHomePage',
  page() {
    return {
      title: this.$t('titles.home'),
    };
  },
  components: {
    FontAwesomeIcon,
    BlockingContainer,
    FileDownloadLink,
    Layout,
  },
  data() {
    return {
      records: [],
      options: {
        url: 'data-source',
      },
      sortBy: 'id',
      sortDesc: false,
      totalRows: null,
      perPage: 20,
      currentPage: 1,
      fieldsTable: [
        { key: 'created_at', label: this.$t('table_labels.date'), sortable: false, formatter: this.formatDate },
        { key: 'title', label: this.$t('table_labels.topic'), sortable: false },
        { key: 'attachment', label: this.$t('table_labels.attachment'), sortable: false },
        { key: 'actions', label: this.$t('table_labels.actions'), sortable: false },
      ],
      isBusy: false,
    };
  },
  computed: {
    sortDir() {
      return this.sortDesc ? '-' : '';
    },
  },
  methods: {
    itemsProvider(ctx) {
      return NewsRecordModel.orderBy(this.sortDir + ctx.sortBy)
        .page(this.currentPage)
        .limit(ctx.perPage)
        .get()
        .then((response) => {
          const items = response.data;
          this.totalRows = response.meta.total;
          return items || [];
        })
        .catch(() => []);
    },
    formatDate(value) {
      return DateTime.fromSQL(value).toFormat('dd.MM.yyyy');
    },
  },
};
</script>

<template>
  <Layout>
    <b-row class="mb-4">
      <b-col>
        <header class="d-flex card-header text-white p-0 justify-content-between pr-0 bg-dark">
          <h5 class="p-2-5 mb-0">{{ $t('titles.news') }}</h5>
        </header>
      </b-col>
    </b-row>
     <div id="news-index" class="position-relative">
      <BlockingContainer v-if="$wait.is('news processing') || isBusy"></BlockingContainer>
      <b-table
        ref="asyncTable"
        small
        striped
        :items="itemsProvider"
        stacked="md"
        :busy.sync="isBusy"
        :current-page="currentPage"
        :per-page="perPage"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        :fields="fieldsTable"
        :no-sort-reset="true"
        :show-empty="true"
      >
        <!-- custom sorting icons -->
        <template #head()="{ label, field: { key, sortable } }">
          {{ label }}
          <template v-if="sortable">
            <!-- Custom icons -->
            <template>
              <FontAwesomeIcon v-if="sortBy !== key" :icon="['fas', 'arrows-alt-v']" />
              <FontAwesomeIcon v-else-if="sortDesc" :icon="['fas', 'arrow-down']" />
              <FontAwesomeIcon v-else :icon="['fas', 'arrow-up']" />
            </template>
          </template>
        </template>
        <template v-slot:cell(client)="row">
          <div v-can:doAccountingOrSuperAdministrate :class="$style.statusContainer">
            <div v-show="!$wait.is(`statusLoader${row.item.id}`)">
              <b-form-checkbox v-model="row.item.client" :value="1" :unchecked-value="0" @change="updateItem(row.item)"> </b-form-checkbox>
            </div>

            <FontAwesomeIcon v-show="$wait.is(`statusLoader${row.item.id}`)" :class="$style.statuLoadingIcon" :icon="['fas', 'spinner']" pulse />
          </div>
          <div v-can:doAccountingOrSuperAdministrate.not>
            <FontAwesomeIcon v-if="parseInt(row.item.client, 10)" class="text-success" :icon="['fas', 'check-circle']" />
            <FontAwesomeIcon v-else class="text-danger" :icon="['fas', 'times-circle']" />
          </div>
        </template>
        <template v-slot:cell(attachment)="row">
          <FileDownloadLink v-if="row.item.attachment" :file="row.item.attachment" :removable="false" />
          <template v-else>-</template>
        </template>
        <template v-slot:cell(actions)="row">
          <div class="d-flex">
            <b-btn class="mr-2" size="sm" variant="outline-primary" :to="{ name: 'news.show', params: { id: row.item.id } }">
              <FontAwesomeIcon :icon="['fas', 'eye']" />
            </b-btn>
          </div>
        </template>
        <template v-slot:cell(empty)="row">
          <div class="text-center">{{ $t('messages.no_items') }}</div>
        </template>
      </b-table>
    </div>
    <b-row>
      <b-col sm="12">
        <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" />
      </b-col>
    </b-row>
  </Layout>
</template>
